import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, newIdempotencyKey } from '@/lib/api'
import type { Contest, ContestEntry, PageMeta } from '@/lib/types'

interface ContestList {
  contests: Contest[]
  meta: PageMeta
}

export function useContests(filters: { q?: string; category?: string; status?: string; page?: number }) {
  return useQuery({
    queryKey: ['contests', filters],
    queryFn: () => api.get<ContestList>('/contests', { query: { ...filters, per_page: 12 } }),
  })
}

export function useMyContests() {
  return useQuery({
    queryKey: ['my-contests'],
    queryFn: () => api.get<ContestList>('/me/contests', { query: { per_page: 50 } }),
  })
}

export function useContest(contestId: string) {
  return useQuery({
    queryKey: ['contest', contestId],
    queryFn: () => api.get<{ contest: Contest; is_organizer: boolean }>(`/contests/${contestId}`),
  })
}

export interface ContestInput {
  title: string
  description: string
  category: string
  skills: string[]
  prize_amount: number
  closes_at?: string
}

export function useCreateContest() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: ContestInput) => api.post<{ contest: Contest }>('/contests', input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['my-contests'] }),
  })
}

function useContestMutation<TInput, TData>(contestId: string, mutationFn: (input: TInput) => Promise<TData>) {
  const client = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['contest', contestId] })
      client.invalidateQueries({ queryKey: ['my-contests'] })
      client.invalidateQueries({ queryKey: ['contests'] })
      client.invalidateQueries({ queryKey: ['wallet'] })
    },
  })
}

export function useFundContest(contestId: string) {
  return useContestMutation<void, { contest: Contest }>(contestId, () =>
    api.post<{ contest: Contest }>(`/contests/${contestId}/fund`, undefined, {
      // Hadiah hanya boleh terkunci satu kali; kunci idempotensi yang
      // memastikannya meski permintaan diulang jaringan.
      idempotencyKey: newIdempotencyKey(),
    }),
  )
}

export interface SubmitEntryInput {
  title: string
  note: string
  /** Tautan karya, misalnya Google Drive, Figma, atau Behance. */
  url?: string
}

export function useSubmitEntry(contestId: string) {
  return useContestMutation<SubmitEntryInput, { entry: ContestEntry }>(contestId, ({ url, ...input }) =>
    api.post<{ entry: ContestEntry }>(`/contests/${contestId}/entries`, {
      ...input,
      // Berkasnya sendiri tetap berada di tempat peserta menyimpannya;
      // yang kami simpan hanya tautannya. Sisi peladen memeriksa skemanya
      // sebelum tautan itu pernah ditampilkan sebagai tautan yang dapat
      // diklik.
      files: url?.trim() ? [{ name: 'Karya', url: url.trim() }] : [],
    }),
  )
}

export function useShortlistEntry(contestId: string) {
  return useContestMutation<string, void>(contestId, (entryId: string) =>
    api.post<void>(`/contests/${contestId}/entries/${entryId}/shortlist`),
  )
}

export interface AwardResponse {
  contest: Contest
  winner: {
    participant: string
    gross_amount: number
    platform_fee: number
    tax_withheld: number
    net_to_winner: number
  }
  ledger_transaction_id: string
}

export function useAwardContest(contestId: string) {
  return useContestMutation<string, AwardResponse>(contestId, (entryId: string) =>
    api.post<AwardResponse>(`/contests/${contestId}/entries/${entryId}/award`, undefined, {
      idempotencyKey: newIdempotencyKey(),
    }),
  )
}

export function useJudgingMode(contestId: string) {
  return useContestMutation<void, { contest: Contest }>(contestId, () =>
    api.post<{ contest: Contest }>(`/contests/${contestId}/judging`),
  )
}

export function useCancelContest(contestId: string) {
  return useContestMutation<string, { contest: Contest }>(contestId, (reason: string) =>
    api.post<{ contest: Contest }>(`/contests/${contestId}/cancel`, { reason }),
  )
}
